// Voice Log — hands-free time logging. Staff tap the big button, say something
// like "CCM 20 minutes, caregiver discussion, for John Smith", tap to finish;
// the server transcribes + auto-commits asynchronously. Memos that need a
// human fix expand into a review card (transcript + audio + editable fields).
// Admins additionally see a practice-wide review queue.
//
// PHI: recorded audio lives ONLY in memory (Blob) — never IndexedDB or
// localStorage. Failed uploads are retryable until the tab closes (a
// beforeunload warning covers the gap).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createVoiceMemo, listVoiceMemos, getVoiceMemo, commitVoiceMemo,
  discardVoiceMemo, voiceMemoAudioUrl, rerouteVoiceMemo, resolveVoiceMemoNote,
  transferVoiceMemoToOrderChat, undoVoiceMemoAppointment,
} from '../lib/voice-memo-api';
import { ccmApi } from '../lib/api';
import { createVoiceRecorder, blobToBase64 } from '../lib/voice-recorder';
import { formatDob } from '../lib/order-chat-patient-pin';
import { commitBannerText } from '../lib/voice-memo-commit-banner.js';
import '../styles/voice-log.css';

const MAX_SECONDS = 90;
const COUNTDOWN_AFTER = 60; // show the "Ns left" badge once past this
const LIST_LIMIT = 15;

// 'committing' polls too: a stranded manual commit is reclaimed server-side
// within minutes — the page must keep watching to show the outcome.
const NON_TERMINAL = new Set(['uploaded', 'transcribing', 'transcribed', 'processing', 'committing']);

// Must match NOTE_AI_MARKER in server/services/voiceMemos/process.js — the
// editor strips it (the server re-appends idempotently on commit).
const NOTE_AI_MARKER = '[Drafted from a voice memo — AI-transcribed and cleaned. Verify before signing.]';
function stripNoteMarker(body) {
  return String(body || '').replace(NOTE_AI_MARKER, '').trimEnd();
}

// Server reviewReasons codes → human labels.
const REVIEW_REASON_LABELS = {
  empty_transcript: 'Nothing was heard',
  parse_failed: "Couldn't understand the memo",
  no_patient_name: 'No patient name heard',
  multiple_matches: 'More than one matching patient',
  patient_not_found: 'No enrolled patient matched',
  missing_minutes: 'No minutes stated',
  invalid_minutes: 'Minutes out of range',
  missing_activity_type: 'No activity type stated',
  program_ambiguous: 'Patient is in both CCM and BHI — pick one',
  program_not_enrolled: "Patient isn't in that program",
  not_enrolled: "Patient isn't enrolled",
  transcribe_failed: "Audio couldn't be transcribed",
  processing_error: 'Processing failed',
  intent_unsupported: "This memo's request isn't supported yet — handle it manually",
  intent_unclear: "Couldn't tell what kind of memo this is — choose below",
  intent_unavailable: 'Memo routing was unavailable — choose what this memo is',
  note_drafts_disabled: "Chart-note drafts from memos aren't enabled for this practice",
  note_confirm_required: 'Note detected — review the draft and confirm',
  note_integrity_check: 'The cleaned note differs from the dictation (numbers/negations) — verify the text',
  note_too_long: 'Note is too long for an Elation note — shorten it',
  name_mismatch: "Spoken name isn't an exact match — confirm the patient",
  no_elation_link: 'Patient has no linked Elation chart',
  recorder_not_mapped: 'Recorder has no Elation clinician identity for this note type',
  note_maybe_created: 'A draft may already exist in Elation — verify the chart before retrying',
  note_rejected: 'Elation rejected the note',
  orders_disabled: "Order memos aren't enabled for this practice",
  order_confirm_required: 'Order detected — review the bundle and confirm',
  order_needs_patient: 'Confirm which patient these orders are for',
  order_clarification: 'The order needs more detail — see the question below, or handle it in Order Chat',
  order_stale: 'The staged orders changed — review the refreshed card and confirm again',
  order_busy: 'These orders are being filed right now — reload in a moment',
  order_types_unsupported: "Some dictated items can't ride an order memo (appointments, chart writes) — handle those separately",
  order_partial_failure: 'Some orders failed — review the detail and confirm again (created ones are never duplicated)',
  order_partial_indeterminate: 'Some orders may or may not exist in Elation — verify the chart before confirming again',
  orderchat_permission_required: 'Placing orders requires the Order Chat permission',
  order_multi_patient: 'Multiple patients in one dictation — place it through Order Chat, then mark this handled',
  order_transfer_abandoned: 'The Order Chat thread for this memo was never finished — review it again here',
  order_transfer_failed: 'The hand-off to Order Chat did not complete — try again',
  order_transfer_lost: 'The Order Chat thread was deleted — VERIFY IN ELATION whether these orders were already placed before confirming again',
  appointments_disabled: "Appointment memos aren't enabled for this practice",
  scheduling_disabled: "Voice scheduling isn't enabled for this practice",
  appt_confirm_required: 'Appointment request — review the details and confirm (this writes to the schedule immediately)',
  appt_needs_patient: 'Confirm which patient this appointment is for',
  appt_target_not_found: "Couldn't find the appointment this memo refers to — pick it below or handle in Elation",
  appt_target_ambiguous: 'More than one appointment matches — pick the right one below',
  appt_target_past: 'The referenced appointment has already occurred — nothing to change',
  appt_time_expired: 'The dictated time has passed — re-dictate or book in Elation',
  appt_undo_blocked: 'This booking can no longer be undone from here — adjust it in Elation',
};

const INTENT_CHIP = { timelog: 'Time', note: 'Note', order: 'Order', appointment: 'Appt', unclear: '?', unavailable: '?', unclassified: '…' };

// Standard activity types (mirrors the quick-log presets' type values), plus a
// free-text escape hatch in the select.
const ACTIVITY_TYPES = ['Care Coordination', 'Medication Management', 'Lab/Test Review', 'Care Plan Update'];
const OTHER_ACTIVITY = '__other__';

function mmss(sec) {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function whenStr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function apptWhenLabel(localWhen) {
  if (!localWhen) return '';
  const d = new Date(`${localWhen}:00`);
  if (Number.isNaN(d.getTime())) return localWhen.replace('T', ' ');
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// "20m Care Coordination" / "Book Jul 30 2:00 PM · follow-up" — the parsed
// summary on committed rows.
function parsedSummary(memo) {
  const p = memo && memo.parsed;
  if (!p) return '';
  if (memo.intent === 'appointment') {
    const verb = { book: 'Book', reschedule: 'Move', cancel: 'Cancel' }[p.action] || 'Appt';
    const when = apptWhenLabel(p.when);
    return [verb, when, p.reason].filter(Boolean).join(' · ');
  }
  const bits = [];
  if (p.minutes) bits.push(`${p.minutes}m`);
  if (p.activity_type) bits.push(p.activity_type);
  if (p.program) bits.push(p.program.toUpperCase());
  return bits.join(' ');
}

function StatusChip({ status }) {
  if (NON_TERMINAL.has(status)) {
    return (
      <span className="voicelog-chip voicelog-chip--processing">
        <span className="voicelog-spinner dark" aria-hidden="true" style={{ width: 11, height: 11 }} />
        Processing…
      </span>
    );
  }
  if (status === 'committed') return <span className="voicelog-chip voicelog-chip--committed">Logged ✓</span>;
  if (status === 'transferred') return <span className="voicelog-chip voicelog-chip--processing">In Order Chat</span>;
  if (status === 'needs_review') return <span className="voicelog-chip voicelog-chip--review">Needs review</span>;
  if (status === 'failed') return <span className="voicelog-chip voicelog-chip--failed">Failed</span>;
  if (status === 'discarded') return <span className="voicelog-chip voicelog-chip--discarded">Discarded</span>;
  return <span className="voicelog-chip voicelog-chip--processing">{status}</span>;
}

// Expandable fix-up card for a needs_review / failed TIMELOG memo. Lazy-loads
// the detail (transcript, match, audio) on first expand — this component only
// mounts when the row is expanded.
function ReviewCard({ memo, onCommitted, onDiscarded, onRerouted }) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [minutes, setMinutes] = useState('');
  const [activitySel, setActivitySel] = useState('');
  const [activityCustom, setActivityCustom] = useState('');
  const [program, setProgram] = useState(null);
  const [notes, setNotes] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [commitError, setCommitError] = useState('');
  const [commitCode, setCommitCode] = useState('');

  useEffect(() => {
    let cancelled = false;
    getVoiceMemo(memo.id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        const p = d.parsed || {};
        setMinutes(p.minutes ? String(p.minutes) : '');
        if (p.activity_type && ACTIVITY_TYPES.includes(p.activity_type)) {
          setActivitySel(p.activity_type);
        } else if (p.activity_type) {
          setActivitySel(OTHER_ACTIVITY);
          setActivityCustom(p.activity_type);
        }
        const parsedProgram = p.program === 'ccm' || p.program === 'bhi' ? p.program : null;
        setNotes(p.notes || '');
        if (d.match && d.match.status === 'one' && d.match.patient) {
          setPatientId(d.match.patient.id);
          // Dual-enrolled + no spoken program → pre-select CCM (practice
          // default; mirrors the worker's dual_default).
          const progs = d.match.patient.programs;
          setProgram(parsedProgram || (progs && progs.ccm && progs.bhi ? 'ccm' : null));
        } else {
          setProgram(parsedProgram);
        }
      })
      .catch((e) => { if (!cancelled) setLoadError(e.message || 'Could not load this memo.'); });
    return () => { cancelled = true; };
  }, [memo.id]);

  const reasons = (memo.reviewReasons || []).map((c) => REVIEW_REASON_LABELS[c] || c);
  const match = (detail && detail.match) || {};
  // Selectable enrolled patients: the single match, or the ambiguous candidates.
  const candidates = match.status === 'one' && match.patient
    ? [match.patient]
    : (match.candidates || []);
  const directoryCandidates = match.directoryCandidates || [];
  const reasonSet = new Set(memo.reviewReasons || []);
  // The SELECTED patient's program enrollment (candidates carry programs
  // flags; memos matched before flags existed just won't have them).
  const selected = candidates.find((p) => p.id === patientId) || null;
  const selectedPrograms = (selected && selected.programs) || null;
  const selectedDual = !!(selectedPrograms && selectedPrograms.ccm && selectedPrograms.bhi);
  const showProgram = !!program
    || selectedDual
    || reasonSet.has('program_ambiguous')
    || reasonSet.has('program_not_enrolled')
    || commitCode === 'program_ambiguous'
    || commitCode === 'program_not_enrolled'
    || !!(detail && detail.parsed && detail.parsed.program);
  // Dual enrollment (or the server telling us it's ambiguous) → a program
  // choice is required before Log it enables.
  const needsProgram = !program && (selectedDual || commitCode === 'program_ambiguous');

  const minutesInt = parseInt(minutes, 10);
  const minutesOk = Number.isInteger(minutesInt) && minutesInt >= 1 && minutesInt <= 300;
  const activityType = activitySel === OTHER_ACTIVITY ? activityCustom.trim() : activitySel;
  const canCommit = !!patientId && minutesOk && !needsProgram && !busy;

  function pickPatient(p) {
    setPatientId(p.id);
    setCommitCode('');
    const progs = p.programs;
    if (!progs) return;
    setProgram((cur) => {
      // Carried-over program this patient isn't in → drop it; dual-enrolled
      // with nothing (valid) selected → pre-select CCM (practice default).
      const valid = cur && progs[cur] ? cur : null;
      return valid || (progs.ccm && progs.bhi ? 'ccm' : null);
    });
  }

  function stepMinutes(delta) {
    const cur = Number.isInteger(minutesInt) ? minutesInt : 0;
    setMinutes(String(Math.max(1, Math.min(300, cur + delta))));
  }

  async function commit() {
    if (!canCommit) return;
    setBusy(true);
    setCommitError('');
    try {
      const res = await commitVoiceMemo(memo.id, {
        patientId,
        minutes: minutesInt,
        program: showProgram ? (program || null) : null,
        activityType,
        notes,
      });
      onCommitted(memo.id, res);
    } catch (e) {
      // 422 { error, code } → inline text; 409 = memo moved on under us.
      // program_ambiguous / program_not_enrolled additionally reveal the
      // program picker (older memos' match rows lack programs flags).
      setCommitError(e.message || 'Commit failed.');
      setCommitCode((e.body && e.body.code) || '');
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (busy) return;
    setBusy(true);
    setCommitError('');
    try {
      await discardVoiceMemo(memo.id);
      onDiscarded(memo.id);
    } catch (e) {
      setCommitError(e.message || 'Discard failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px', fontSize: 13 }}>
      {reasons.length > 0 && (
        <div style={{
          marginBottom: 10, padding: '8px 10px', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: 8, color: '#92400e',
        }}>
          {reasons.map((r) => <div key={r}>• {r}</div>)}
        </div>
      )}

      {loadError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{loadError}</div>}
      {!detail && !loadError && (
        <div role="status" aria-live="polite" aria-label="Loading memo" className="skeleton">
          <div className="sk-line w70" />
          <div className="sk-line w40" />
        </div>
      )}

      {detail && (
        <>
          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          {detail.error && (
            <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>{detail.error}</div>
          )}
          <audio
            controls
            preload="none"
            src={voiceMemoAudioUrl(memo.id)}
            style={{ width: '100%', marginBottom: 12 }}
          />

          {onRerouted && <ReroutePills memo={memo} busy={busy} onRerouted={onRerouted} />}

          {/* Patient picker */}
          <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Patient</div>
          {candidates.length === 0 && directoryCandidates.length === 0 && (
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              No matching enrolled patient — discard this memo and log the time manually.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                className={'voicelog-candidate' + (patientId === p.id ? ' is-selected' : '')}
                onClick={() => pickPatient(p)}
                aria-pressed={patientId === p.id}
              >
                {p.firstName} {p.lastName}
                {p.dob ? <span className="dob">DOB {formatDob(p.dob)}</span> : null}
              </button>
            ))}
            {directoryCandidates.map((p) => (
              <button
                key={p.id}
                type="button"
                className="voicelog-candidate"
                disabled
                title="This patient is in the directory but not enrolled — the memo can't be committed to them."
              >
                {p.firstName} {p.lastName}
                {p.dob ? <span className="dob">DOB {formatDob(p.dob)}</span> : null}
                <span className="dob">· not enrolled</span>
              </button>
            ))}
          </div>

          {/* Minutes + activity type */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
            <div>
              <label htmlFor={`vl-min-${memo.id}`} style={{ display: 'block', fontWeight: 600, color: '#111', marginBottom: 6 }}>
                Minutes
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button type="button" className="voicelog-step-btn" aria-label="5 fewer minutes"
                        disabled={busy || (Number.isInteger(minutesInt) && minutesInt <= 1)}
                        onClick={() => stepMinutes(-5)}>−</button>
                <input
                  id={`vl-min-${memo.id}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={300}
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  disabled={busy}
                  style={{
                    width: 72, padding: '8px 10px', borderRadius: 8, textAlign: 'center',
                    border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
                  }}
                />
                <button type="button" className="voicelog-step-btn" aria-label="5 more minutes"
                        disabled={busy}
                        onClick={() => stepMinutes(5)}>+</button>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label htmlFor={`vl-act-${memo.id}`} style={{ display: 'block', fontWeight: 600, color: '#111', marginBottom: 6 }}>
                Activity type
              </label>
              <select
                id={`vl-act-${memo.id}`}
                value={activitySel}
                disabled={busy}
                onChange={(e) => setActivitySel(e.target.value)}
                style={{
                  width: '100%', padding: '9px 10px', borderRadius: 8, background: '#fff',
                  border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
                }}
              >
                <option value="">Choose…</option>
                {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                <option value={OTHER_ACTIVITY}>Other…</option>
              </select>
              {activitySel === OTHER_ACTIVITY && (
                <input
                  type="text"
                  value={activityCustom}
                  disabled={busy}
                  onChange={(e) => setActivityCustom(e.target.value)}
                  placeholder="Describe the activity"
                  aria-label="Custom activity type"
                  style={{
                    width: '100%', marginTop: 6, padding: '8px 10px', borderRadius: 8,
                    border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
                  }}
                />
              )}
            </div>
          </div>

          {/* Program pills — when the memo parsed one, the reasons demand one,
              or the SELECTED patient is enrolled in both CCM and BHI. */}
          {showProgram && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Program</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['ccm', 'bhi'].map((pr) => {
                  const notEnrolled = !!selectedPrograms && !selectedPrograms[pr];
                  return (
                    <button
                      key={pr}
                      type="button"
                      className={'voicelog-pill' + (program === pr ? ' is-selected' : '')}
                      onClick={() => setProgram((cur) => (cur === pr ? null : pr))}
                      aria-pressed={program === pr}
                      disabled={busy || notEnrolled}
                      title={notEnrolled ? `Not enrolled in ${pr.toUpperCase()}` : undefined}
                    >
                      {pr.toUpperCase()}
                    </button>
                  );
                })}
              </div>
              {(selectedDual || needsProgram) && (
                <div style={{ marginTop: 6, fontSize: 12, color: needsProgram ? '#92400e' : '#6b7280' }}>
                  {needsProgram
                    ? 'Enrolled in both CCM and BHI — choose which program this time counts toward.'
                    : `Enrolled in both CCM and BHI — this time will count toward ${(program || 'ccm').toUpperCase()}.`}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: 12 }}>
            <label htmlFor={`vl-notes-${memo.id}`} style={{ display: 'block', fontWeight: 600, color: '#111', marginBottom: 6 }}>
              Notes
            </label>
            <input
              id={`vl-notes-${memo.id}`}
              type="text"
              value={notes}
              disabled={busy}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional description"
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8,
                border: '1px solid #d1d5db', fontSize: 14, fontFamily: 'inherit',
              }}
            />
          </div>

          {commitError && (
            <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{commitError}</div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              disabled={busy}
              onClick={discard}
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#b91c1c', background: '#fff',
                border: '1px solid #fecaca', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Discard
            </button>
            <button
              type="button"
              disabled={!canCommit}
              onClick={commit}
              style={{
                padding: '9px 18px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#fff', background: canCommit ? '#0f766e' : '#9ca3af',
                border: 'none', borderRadius: 8, cursor: canCommit ? 'pointer' : 'not-allowed',
              }}
            >
              {busy ? 'Working…' : 'Log it'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// "Treat as: Time | Note" — human intent correction for a parked memo. The
// server wipes the derived state and the worker re-extracts on its next tick.
function ReroutePills({ memo, busy, onRerouted }) {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  async function reroute(intent) {
    if (working || busy || intent === memo.intent) return;
    setWorking(true);
    setError('');
    try {
      await rerouteVoiceMemo(memo.id, intent);
      onRerouted(memo.id, intent);
    } catch (e) {
      setError(e.message || 'Reroute failed.');
    } finally {
      setWorking(false);
    }
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Treat this memo as</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[['timelog', 'Time log'], ['note', 'Chart note'], ['order', 'Order'], ['appointment', 'Appointment']].map(([intent, label]) => (
          <button
            key={intent}
            type="button"
            className={'voicelog-pill' + (memo.intent === intent ? ' is-selected' : '')}
            disabled={working || busy || memo.intent === intent}
            onClick={() => reroute(intent)}
          >
            {label}
          </button>
        ))}
      </div>
      {error && <div role="alert" style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

// Banner + resolution actions for an indeterminate note attempt ("Elation may
// or may not have the draft"). No silent retry: link a verified existing note,
// authorize ONE new attempt (admin), or mark it handled outside the system.
function IndeterminateNoteBanner({ memo, isAdmin, onCommitted, onDiscarded }) {
  const [linkId, setLinkId] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');

  async function act(payload) {
    if (working) return;
    setWorking(true);
    setError('');
    try {
      const res = await resolveVoiceMemoNote(memo.id, payload);
      if (res.status === 'committed') onCommitted(memo.id, res);
      else onDiscarded(memo.id);
    } catch (e) {
      setError(e.message || 'Resolution failed.');
    } finally {
      setWorking(false);
    }
  }

  return (
    <div style={{
      marginBottom: 12, padding: '10px 12px', background: '#fef2f2',
      border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 12,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        A draft may already exist in Elation — open the patient's chart and check before doing anything else.
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <input
          type="text"
          inputMode="numeric"
          value={linkId}
          disabled={working}
          onChange={(e) => setLinkId(e.target.value)}
          placeholder="Elation note id"
          aria-label="Existing Elation note id"
          style={{ width: 130, padding: '6px 8px', borderRadius: 6, border: '1px solid #fca5a5', fontSize: 12 }}
        />
        <button type="button" className="btn sm" disabled={working || !/^\d+$/.test(linkId.trim())}
                onClick={() => act({ action: 'link', noteId: linkId.trim() })}>
          It exists — link it
        </button>
        {isAdmin && (
          <button type="button" className="btn sm" disabled={working}
                  onClick={() => act({ action: 'retry' })}>
            Verified none exists — create it
          </button>
        )}
        <button type="button" className="btn ghost sm" disabled={working}
                onClick={() => act({ action: 'handled' })}>
          Handled elsewhere
        </button>
      </div>
      {error && <div role="alert" style={{ marginTop: 6 }}>{error}</div>}
    </div>
  );
}

// Review card for intent='note': confirm/fix the cleaned chart-note draft.
// The dictated transcript stays visible under the editable body so the
// reviewer can verify the AI cleanup against what was actually said.
function NoteReviewCard({ memo, isAdmin, onCommitted, onDiscarded, onRerouted }) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [body, setBody] = useState('');
  const [noteKind, setNoteKind] = useState('visit');
  const [patientId, setPatientId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [commitError, setCommitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getVoiceMemo(memo.id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        const p = d.parsed || {};
        setBody(stripNoteMarker((d.commitPayload && d.commitPayload.body) || p.body || ''));
        setNoteKind(p.noteType === 'nonvisit' ? 'nonvisit' : 'visit');
        if (d.match && d.match.status === 'one' && d.match.patient) setPatientId(d.match.patient.id);
      })
      .catch((e) => { if (!cancelled) setLoadError(e.message || 'Could not load this memo.'); });
    return () => { cancelled = true; };
  }, [memo.id]);

  const reasons = (memo.reviewReasons || []).map((c) => REVIEW_REASON_LABELS[c] || c);
  const match = (detail && detail.match) || {};
  const candidates = match.status === 'one' && match.patient ? [match.patient] : (match.candidates || []);
  const attempts = (detail && detail.noteAttempts) || [];
  const indeterminate = attempts.some((a) => a && (a.state === 'indeterminate' || a.state === 'posting'));
  const canCommit = !!patientId && !!body.trim() && !indeterminate && !busy;

  async function commit() {
    if (!canCommit) return;
    setBusy(true);
    setCommitError('');
    try {
      const res = await commitVoiceMemo(memo.id, { patientId, noteKind, body });
      onCommitted(memo.id, res);
    } catch (e) {
      setCommitError(e.message || 'Commit failed.');
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (busy) return;
    setBusy(true);
    setCommitError('');
    try {
      await discardVoiceMemo(memo.id);
      onDiscarded(memo.id);
    } catch (e) {
      setCommitError(e.message || 'Discard failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px', fontSize: 13 }}>
      {reasons.length > 0 && (
        <div style={{
          marginBottom: 10, padding: '8px 10px', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: 8, color: '#92400e',
        }}>
          {reasons.map((r) => <div key={r}>• {r}</div>)}
        </div>
      )}
      {loadError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{loadError}</div>}
      {!detail && !loadError && (
        <div role="status" aria-live="polite" aria-label="Loading memo" className="skeleton">
          <div className="sk-line w70" />
          <div className="sk-line w40" />
        </div>
      )}

      {detail && (
        <>
          {indeterminate && (
            <IndeterminateNoteBanner memo={memo} isAdmin={isAdmin} onCommitted={onCommitted} onDiscarded={onDiscarded} />
          )}

          <ReroutePills memo={memo} busy={busy} onRerouted={onRerouted} />

          <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Patient</div>
          {candidates.length === 0 && (
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              No matching enrolled patient — discard this memo and write the note in Elation directly.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                className={'voicelog-candidate' + (patientId === p.id ? ' is-selected' : '')}
                onClick={() => setPatientId(p.id)}
                aria-pressed={patientId === p.id}
              >
                {p.firstName} {p.lastName}
                {p.dob ? <span className="dob">DOB {formatDob(p.dob)}</span> : null}
              </button>
            ))}
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Note type</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['visit', 'Visit note'], ['nonvisit', 'Non-visit (phone) note']].map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  className={'voicelog-pill' + (noteKind === k ? ' is-selected' : '')}
                  onClick={() => setNoteKind(k)}
                  aria-pressed={noteKind === k}
                  disabled={busy}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label htmlFor={`vl-note-${memo.id}`} style={{ display: 'block', fontWeight: 600, color: '#111', marginBottom: 6 }}>
              Note text <span style={{ fontWeight: 400, color: '#6b7280' }}>(AI-cleaned — verify against the dictation below)</span>
            </label>
            <textarea
              id={`vl-note-${memo.id}`}
              value={body}
              disabled={busy}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 8,
                border: '1px solid #d1d5db', fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
              }}
            />
          </div>

          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          <audio controls preload="none" src={voiceMemoAudioUrl(memo.id)} style={{ width: '100%', marginBottom: 12 }} />

          {commitError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{commitError}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              disabled={busy}
              onClick={discard}
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#b91c1c', background: '#fff',
                border: '1px solid #fecaca', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Discard
            </button>
            <button
              type="button"
              disabled={!canCommit}
              onClick={commit}
              title={indeterminate ? 'Resolve the possible existing draft first' : undefined}
              style={{
                padding: '9px 18px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#fff', background: canCommit ? '#0f766e' : '#9ca3af',
                border: 'none', borderRadius: 8, cursor: canCommit ? 'pointer' : 'not-allowed',
              }}
            >
              {busy ? 'Working…' : 'Create note draft'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// One order, summarized read-only (editing happens in Order Chat — the memo
// card is a confirm surface, not an editor).
//
// R-CF-2: when the memo carries a STAGED card, its `summary` IS the line. That
// string is the readback the server speaks — the matched catalog product, the
// recipient the letter will actually be addressed to, the codes — and it
// describes the session the confirm will file. The dictated fields below are
// the fallback for a memo whose staged session could not be read (and for rows
// staged before this shipped): what was SAID, which is not always what would
// be created.
function orderSummaryLine(o, card) {
  if (card && typeof card.summary === 'string' && card.summary) return card.summary;
  if (!o || !o.type) return '';
  if (o.type === 'lab') {
    const set = o.lab_order_set_name || o.labOrderSetName;
    return set ? `Lab order set "${set}"` : `Labs: ${(o.tests || []).join(', ') || '—'}`;
  }
  if (['imaging', 'cardiac', 'pulmonary', 'sleep'].includes(o.type)) {
    return `${o.type[0].toUpperCase()}${o.type.slice(1)}: ${(o.tests || []).join(', ') || '—'}`;
  }
  if (o.type === 'medication') {
    return `Medication: ${o.drug_name || '—'}${o.dose ? ` ${o.dose}` : ''}${o.frequency ? `, ${o.frequency}` : ''}${o.order_type === 'Refill' ? ' (refill)' : ''}`;
  }
  if (o.type === 'referral') return `Referral: ${o.specialty || '—'}${o.recipient ? ` → ${o.recipient}` : ''}`;
  if (o.type === 'quest_homebound') {
    const names = (o.quest_tests || []).map((t) => t && t.name).filter(Boolean);
    return `Home draw: ${names.join(', ') || '—'}`;
  }
  return `${o.type}`;
}

// The lines the order card lists. The STAGED session is the bundle the confirm
// files, so its cards are the list even where they differ from the dictation —
// a referral the engine could not place is simply not there, and the provider
// must be able to see that before they confirm. The dx suffix is dropped on a
// staged line because `summary` already carries the indication.
// → [{ text, dx }]
function memoOrderLines(detail, summaryLine) {
  const staged = detail && detail.staged;
  if (staged && Array.isArray(staged.cards)) {
    return staged.cards.map((c) => ({ text: summaryLine(null, c), dx: '' }));
  }
  const orders = (detail && detail.parsed && Array.isArray(detail.parsed.orders))
    ? detail.parsed.orders : [];
  return orders.map((o) => ({ text: summaryLine(o), dx: o.dxLabel || o.diagnosis || '' }));
}

// Is Confirm offered? Only for a staged session the SERVER would accept
// (R-CF-2 / R-S5-52): a bundle still holding an open question in Order Chat is
// answered there, not here, and one that has moved on cannot be confirmed at
// all. And only when this card HAS a staged session id to send — the confirm
// carries the id of the session the card displayed and the server files nothing
// without it, so a card that could not render its staged bundle has nothing to
// confirm however many orders were dictated. (Such a card is showing the
// dictation; confirming it would be the raw-parse confirm R-CF-2 forbids.)
function memoCanCommit(detail, busy) {
  if (busy) return false;
  const staged = detail && detail.staged;
  if (!staged || !staged.sessionId) return false;
  return ['awaiting_confirmation', 'awaiting_patient', 'partially_sent'].includes(staged.state);
}

// What the card says INSTEAD of the confirm buttons — one sentence naming where
// the bundle can be finished. '' when Confirm is offered.
function memoStagedNotice(detail) {
  if (detail && detail.stagedError) {
    return 'Couldn’t load the staged orders — reload, or handle this memo in Order Chat.';
  }
  const staged = detail && detail.staged;
  if (!staged || !staged.sessionId) {
    return detail
      ? 'No staged orders to confirm — open this memo in Order Chat, then mark it handled.'
      : '';
  }
  const state = staged.state;
  if (!state) return '';
  if (state === 'awaiting_selection') return 'Needs a pick in Order Chat — use “Open in Order Chat”.';
  if (state === 'awaiting_clarification' || state === 'awaiting_med_details') {
    return 'Needs a clarification in Order Chat — use “Open in Order Chat”.';
  }
  if (['awaiting_confirmation', 'awaiting_patient', 'partially_sent'].includes(state)) return '';
  return 'These staged orders are no longer open — reload the memo to see the refreshed card.';
}

// Review card for intent='order': confirm (or fix the patient for) the staged
// draft bundle. Orders NEVER auto-commit — this card is the only path, and
// the server re-validates everything (permission, patient bridge, delivery
// policy, per-order attempt ledger).
function OrderReviewCard({ memo, onCommitted, onDiscarded, onRerouted, onTransferred }) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [commitError, setCommitError] = useState('');

  async function toOrderChat() {
    if (busy) return;
    setBusy(true);
    setCommitError('');
    try {
      const res = await transferVoiceMemoToOrderChat(memo.id);
      onTransferred(memo.id, res);
    } catch (e) {
      setCommitError(e.message || 'Transfer failed.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    getVoiceMemo(memo.id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        if (d.match && d.match.status === 'one' && d.match.patient) setPatientId(d.match.patient.id);
      })
      .catch((e) => { if (!cancelled) setLoadError(e.message || 'Could not load this memo.'); });
    return () => { cancelled = true; };
  }, [memo.id]);

  const reasons = (memo.reviewReasons || []).map((c) => REVIEW_REASON_LABELS[c] || c);
  const match = (detail && detail.match) || {};
  const candidates = match.status === 'one' && match.patient ? [match.patient] : (match.candidates || []);
  const orderLines = memoOrderLines(detail, orderSummaryLine);
  const question = detail && detail.error;
  const canCommit = memoCanCommit(detail, busy);
  const stagedNotice = memoStagedNotice(detail);

  async function commit(delivery) {
    if (!canCommit) return;
    setBusy(true);
    setCommitError('');
    try {
      // The id of the session THIS card rendered travels with the confirm: the
      // memo's own pointer can have moved since (another tab's card read
      // refreshes a dead session and re-points the memo), and the server files
      // nothing unless the two still agree.
      const res = await commitVoiceMemo(memo.id, {
        patientId: patientId || undefined,
        delivery,
        sessionId: detail.staged && detail.staged.sessionId,
      });
      onCommitted(memo.id, res);
    } catch (e) {
      setCommitError(e.message || 'Commit failed.');
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (busy) return;
    setBusy(true);
    setCommitError('');
    try {
      await discardVoiceMemo(memo.id);
      onDiscarded(memo.id);
    } catch (e) {
      setCommitError(e.message || 'Discard failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px', fontSize: 13 }}>
      {reasons.length > 0 && (
        <div style={{
          marginBottom: 10, padding: '8px 10px', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: 8, color: '#92400e',
        }}>
          {reasons.map((r) => <div key={r}>• {r}</div>)}
        </div>
      )}
      {loadError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{loadError}</div>}
      {!detail && !loadError && (
        <div role="status" aria-live="polite" aria-label="Loading memo" className="skeleton">
          <div className="sk-line w70" />
          <div className="sk-line w40" />
        </div>
      )}

      {detail && (
        <>
          <ReroutePills memo={memo} busy={busy} onRerouted={onRerouted} />

          <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Patient</div>
          {candidates.length === 0 && (
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              No matching enrolled patient — handle this order in Order Chat or Elation, then mark it handled.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                className={'voicelog-candidate' + (patientId === p.id ? ' is-selected' : '')}
                onClick={() => setPatientId(p.id)}
                aria-pressed={patientId === p.id}
              >
                {p.firstName} {p.lastName}
                {p.dob ? <span className="dob">DOB {formatDob(p.dob)}</span> : null}
              </button>
            ))}
          </div>

          <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Orders ({orderLines.length})</div>
          <div style={{ marginBottom: 12 }}>
            {orderLines.map((line, i) => (
              <div key={i} style={{
                padding: '6px 10px', marginBottom: 6, background: '#f9fafb',
                border: '1px solid #f3f4f6', borderRadius: 8, color: '#374151',
              }}>
                {line.text}
                {line.dx && <span style={{ color: '#6b7280' }}> — for {line.dx}</span>}
              </div>
            ))}
          </div>

          {stagedNotice && (
            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 10 }}>{stagedNotice}</div>
          )}

          {question && (
            <div style={{ color: '#92400e', fontSize: 12, marginBottom: 10 }}>{question}</div>
          )}

          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          <audio controls preload="none" src={voiceMemoAudioUrl(memo.id)} style={{ width: '100%', marginBottom: 12 }} />

          {commitError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{commitError}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={busy}
              onClick={discard}
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#b91c1c', background: '#fff',
                border: '1px solid #fecaca', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Discard
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={toOrderChat}
              title="Move this bundle into Order Chat to edit tests, doses, diagnoses, or destinations before placing it"
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#374151', background: '#fff',
                border: '1px solid #d1d5db', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Open in Order Chat
            </button>
            <button
              type="button"
              disabled={!canCommit}
              onClick={() => commit('send')}
              title="Create the drafts AND sign/transmit where a verified destination exists (referral letters)"
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#0f766e', background: '#fff',
                border: '1px solid #99f6e4', borderRadius: 8, cursor: canCommit ? 'pointer' : 'not-allowed',
              }}
            >
              Create + send now
            </button>
            <button
              type="button"
              disabled={!canCommit}
              onClick={() => commit('draft')}
              style={{
                padding: '9px 18px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#fff', background: canCommit ? '#0f766e' : '#9ca3af',
                border: 'none', borderRadius: 8, cursor: canCommit ? 'pointer' : 'not-allowed',
              }}
            >
              {busy ? 'Working…' : 'Create drafts'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Review card for intent='appointment': the resolved schedule change,
// verified against the dictated words, confirmed with one tap. The confirm
// WRITES LIVE (book/move/cancel) — everything shown here is the gate.
function AppointmentReviewCard({ memo, onCommitted, onDiscarded, onRerouted, onTransferred }) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [targetApptId, setTargetApptId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [commitError, setCommitError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getVoiceMemo(memo.id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        if (d.match && d.match.status === 'one' && d.match.patient) setPatientId(d.match.patient.id);
        if (d.parsed && d.parsed.appointmentId != null) setTargetApptId(String(d.parsed.appointmentId));
      })
      .catch((e) => { if (!cancelled) setLoadError(e.message || 'Could not load this memo.'); });
    return () => { cancelled = true; };
  }, [memo.id]);

  const reasons = (memo.reviewReasons || []).map((c) => REVIEW_REASON_LABELS[c] || c);
  const match = (detail && detail.match) || {};
  const candidates = match.status === 'one' && match.patient ? [match.patient] : (match.candidates || []);
  const p = (detail && detail.parsed) || {};
  const action = p.action || 'book';
  const resolved = p.resolved || {};
  const warnings = Array.isArray(resolved.warnings) ? resolved.warnings : [];
  const targetCandidates = Array.isArray(p.targetCandidates) ? p.targetCandidates : [];
  const needsTarget = action !== 'book' && !targetApptId;
  const canCommit = !!patientId && !needsTarget && !busy;
  const confirmLabel = { book: 'Book appointment', reschedule: 'Move appointment', cancel: 'Cancel appointment' }[action] || 'Confirm';

  async function commit() {
    if (!canCommit) return;
    setBusy(true);
    setCommitError('');
    try {
      const res = await commitVoiceMemo(memo.id, {
        patientId,
        appointmentId: targetApptId || undefined,
      });
      onCommitted(memo.id, res);
    } catch (e) {
      setCommitError(e.message || 'Commit failed.');
    } finally {
      setBusy(false);
    }
  }

  async function toOrderChat() {
    if (busy) return;
    setBusy(true);
    setCommitError('');
    try {
      const res = await transferVoiceMemoToOrderChat(memo.id);
      onTransferred(memo.id, res);
    } catch (e) {
      setCommitError(e.message || 'Transfer failed.');
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (busy) return;
    setBusy(true);
    setCommitError('');
    try {
      await discardVoiceMemo(memo.id);
      onDiscarded(memo.id);
    } catch (e) {
      setCommitError(e.message || 'Discard failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px', fontSize: 13 }}>
      {reasons.length > 0 && (
        <div style={{
          marginBottom: 10, padding: '8px 10px', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: 8, color: '#92400e',
        }}>
          {reasons.map((r) => <div key={r}>• {r}</div>)}
        </div>
      )}
      {loadError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{loadError}</div>}
      {!detail && !loadError && (
        <div role="status" aria-live="polite" aria-label="Loading memo" className="skeleton">
          <div className="sk-line w70" />
          <div className="sk-line w40" />
        </div>
      )}

      {detail && (
        <>
          <ReroutePills memo={memo} busy={busy} onRerouted={onRerouted} />

          <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Patient</div>
          {candidates.length === 0 && (
            <div style={{ color: '#6b7280', marginBottom: 8 }}>
              No matching enrolled patient — handle this in Elation, then mark it handled.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                className={'voicelog-candidate' + (patientId === c.id ? ' is-selected' : '')}
                onClick={() => setPatientId(c.id)}
                aria-pressed={patientId === c.id}
              >
                {c.firstName} {c.lastName}
                {c.dob ? <span className="dob">DOB {formatDob(c.dob)}</span> : null}
              </button>
            ))}
          </div>

          {/* The resolved schedule change — dictated words paired with the
              resolution so the reviewer checks the date math against what
              THEY said, not from memory. */}
          <div style={{
            marginBottom: 12, padding: '8px 10px', background: '#f9fafb',
            border: '1px solid #f3f4f6', borderRadius: 8, color: '#374151',
          }}>
            <div style={{ fontWeight: 600, color: '#111' }}>
              {action === 'book' && `Book: ${apptWhenLabel(p.when)}${resolved.apptType ? ` · ${resolved.apptType}` : p.reason ? ` · ${p.reason}` : ''}${resolved.duration ? ` · ${resolved.duration} min` : ''}${p.mode === 'video' ? ' · video' : ''}`}
              {action === 'reschedule' && `Move to: ${apptWhenLabel(p.when)}`}
              {action === 'cancel' && 'Cancel appointment'}
            </div>
            {p.when_phrase && p.when && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                You said “{p.when_phrase}” → {apptWhenLabel(p.when)}
              </div>
            )}
            {action !== 'book' && p.resolvedTarget && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Target: {new Date(p.resolvedTarget.scheduledDate).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                {p.resolvedTarget.reason ? ` — ${p.resolvedTarget.reason}` : ''}
              </div>
            )}
          </div>

          {warnings.length > 0 && (
            <div style={{
              marginBottom: 12, padding: '8px 10px', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 8, color: '#92400e', fontSize: 12,
            }}>
              Heads up — {warnings.join('; ')}
            </div>
          )}

          {action !== 'book' && targetCandidates.length > 0 && !p.resolvedTarget && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Which appointment?</div>
              {targetCandidates.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={'voicelog-candidate' + (targetApptId === String(a.id) ? ' is-selected' : '')}
                  onClick={() => setTargetApptId(String(a.id))}
                  aria-pressed={targetApptId === String(a.id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 6 }}
                >
                  {new Date(a.scheduledDate).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  {a.reason ? ` — ${a.reason}` : ''}
                </button>
              ))}
            </div>
          )}

          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          <audio controls preload="none" src={voiceMemoAudioUrl(memo.id)} style={{ width: '100%', marginBottom: 12 }} />

          {commitError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{commitError}</div>}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={busy}
              onClick={discard}
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#b91c1c', background: '#fff',
                border: '1px solid #fecaca', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Discard
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={toOrderChat}
              title="Move this into Order Chat to adjust details before confirming"
              style={{
                padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#374151', background: '#fff',
                border: '1px solid #d1d5db', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Open in Order Chat
            </button>
            <button
              type="button"
              disabled={!canCommit}
              onClick={commit}
              style={{
                padding: '9px 18px', fontSize: 13, fontWeight: 600, minHeight: 44,
                color: '#fff',
                background: !canCommit ? '#9ca3af' : (action === 'cancel' ? '#b91c1c' : '#0f766e'),
                border: 'none', borderRadius: 8, cursor: canCommit ? 'pointer' : 'not-allowed',
              }}
            >
              {busy ? 'Working…' : confirmLabel}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Parked order/appointment/unclear memos: no commit path from here (yet) —
// show why, let the reviewer re-route mis-classified ones or mark the memo
// handled once they've done the work in the right tool.
function UnsupportedCard({ memo, onDiscarded, onRerouted }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getVoiceMemo(memo.id).then((d) => { if (!cancelled) setDetail(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [memo.id]);

  const reasons = (memo.reviewReasons || []).map((c) => REVIEW_REASON_LABELS[c] || c);

  async function markHandled() {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await discardVoiceMemo(memo.id);
      onDiscarded(memo.id);
    } catch (e) {
      setError(e.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px', fontSize: 13 }}>
      {reasons.length > 0 && (
        <div style={{
          marginBottom: 10, padding: '8px 10px', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: 8, color: '#92400e',
        }}>
          {reasons.map((r) => <div key={r}>• {r}</div>)}
        </div>
      )}
      {detail && detail.transcript && (
        <div style={{ color: '#374151', fontStyle: 'italic', marginBottom: 8 }}>“{detail.transcript}”</div>
      )}
      <audio controls preload="none" src={voiceMemoAudioUrl(memo.id)} style={{ width: '100%', marginBottom: 12 }} />
      <ReroutePills memo={memo} busy={busy} onRerouted={onRerouted} />
      {error && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          disabled={busy}
          onClick={markHandled}
          style={{
            padding: '9px 16px', fontSize: 13, fontWeight: 600, minHeight: 44,
            color: '#374151', background: '#fff',
            border: '1px solid #d1d5db', borderRadius: 8, cursor: busy ? 'wait' : 'pointer',
          }}
        >
          Mark handled
        </button>
      </div>
    </div>
  );
}

// One-tap undo for a memo-BOOKED appointment (status-flip cancel). The
// server pre-flights: future + still Scheduled/Confirmed, CAS against
// double-undo. reschedule/cancel commits have no undo (not auto-reversible).
function UndoAppointmentButton({ memo }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  async function undo() {
    if (busy || done) return;
    setBusy(true);
    setError('');
    try {
      await undoVoiceMemoAppointment(memo.id);
      setDone(true);
    } catch (e) {
      setError(e.message || 'Undo failed.');
    } finally {
      setBusy(false);
    }
  }
  if (done) {
    return <div style={{ fontSize: 12, color: '#166534', marginBottom: 10 }}>Undone — the appointment was cancelled.</div>;
  }
  return (
    <div style={{ marginBottom: 10 }}>
      <button
        type="button"
        className="btn ghost sm"
        disabled={busy}
        onClick={undo}
        title="Cancels the booked appointment in Elation"
      >
        {busy ? 'Undoing…' : 'Undo booking (cancels the appointment)'}
      </button>
      {error && <div role="alert" style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

// Read-only drill-in for a committed memo: WHO it matched (the fact the row
// summary omits), the parsed facts the entry was logged with, the full
// transcript, and the audio — so a recorder can verify the auto-match without
// leaving the page. Fetches detail lazily on first expand, like ReviewCard.
// Committed memos are immutable server-side (commit 409s), so no actions here
// beyond the Time Logs jump.
function CommittedDetails({ memo, onJumpToTimePatient }) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    getVoiceMemo(memo.id)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setLoadError(e.message || 'Could not load this memo.'); });
    return () => { cancelled = true; };
  }, [memo.id]);

  const match = (detail && detail.match) || {};
  // The patient the entry was actually logged to: prefer the match entry whose
  // id equals committedPatientId (a human review may have picked a candidate
  // other than the top match), else the single auto-match.
  const everyone = [
    ...(match.patient ? [match.patient] : []),
    ...(match.candidates || []),
  ];
  const logged = everyone.find((p) => p.id === memo.committedPatientId)
    || (match.status === 'one' ? match.patient : null);
  const parsed = (detail && detail.parsed) || memo.parsed || {};

  return (
    <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px', fontSize: 13 }}>
      {loadError && <div role="alert" style={{ color: '#b91c1c', marginBottom: 10 }}>{loadError}</div>}
      {!detail && !loadError && (
        <div role="status" aria-live="polite" aria-label="Loading memo" className="skeleton">
          <div className="sk-line w70" />
          <div className="sk-line w40" />
        </div>
      )}

      {detail && memo.intent === 'order' && (
        <>
          <div style={{
            marginBottom: 10, padding: '8px 10px', background: '#f0fdf4',
            border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 12,
          }}>
            {commitBannerText(detail.commitPayload)}
          </div>
          {detail.parsed && detail.parsed.patient_name && (
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
              Patient: <strong>{detail.parsed.patient_name}</strong>
            </div>
          )}
          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          <audio controls preload="none" src={voiceMemoAudioUrl(memo.id)} style={{ width: '100%' }} />
        </>
      )}

      {detail && memo.intent === 'note' && (
        <>
          <div style={{
            marginBottom: 10, padding: '8px 10px', background: '#f0fdf4',
            border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 12,
          }}>
            {(detail.commitPayload && detail.commitPayload.noteKind) === 'nonvisit' ? 'Non-visit' : 'Visit'} note
            draft created{(detail.committedNoteId || memo.committedNoteId) ? ` (Elation note ${detail.committedNoteId || memo.committedNoteId})` : ''} — review and sign it in Elation.
          </div>
          {detail.parsed && detail.parsed.patient_name && (
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
              Patient: <strong>{detail.parsed.patient_name}</strong>
            </div>
          )}
          {detail.commitPayload && detail.commitPayload.body && (
            <div style={{
              whiteSpace: 'pre-wrap', color: '#374151', marginBottom: 12,
              padding: '8px 10px', background: '#f9fafb', borderRadius: 8, border: '1px solid #f3f4f6',
            }}>
              {detail.commitPayload.body}
            </div>
          )}
          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          <audio controls preload="none" src={voiceMemoAudioUrl(memo.id)} style={{ width: '100%' }} />
        </>
      )}

      {detail && memo.intent === 'appointment' && (
        <>
          <div style={{
            marginBottom: 10, padding: '8px 10px', background: '#f0fdf4',
            border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', fontSize: 12,
          }}>
            {(detail.commitPayload && {
              book: `Appointment booked${detail.commitPayload.scheduledISO ? ` for ${new Date(detail.commitPayload.scheduledISO).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}`,
              reschedule: 'Appointment moved',
              cancel: 'Appointment cancelled',
            }[detail.commitPayload.action]) || 'Appointment updated'}
            {detail.commitPayload && detail.commitPayload.undone ? ' — since undone (cancelled)' : ''}.
            {detail.commitPayload && detail.commitPayload.action !== 'book' ? ' Adjust in Elation if this was wrong (not auto-reversible).' : ''}
          </div>
          {detail.parsed && detail.parsed.patient_name && (
            <div style={{ fontSize: 12, color: '#374151', marginBottom: 8 }}>
              Patient: <strong>{detail.parsed.patient_name}</strong>
            </div>
          )}
          {detail.commitPayload && detail.commitPayload.action === 'book' && !detail.commitPayload.undone && (
            <UndoAppointmentButton memo={memo} />
          )}
          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          <audio controls preload="none" src={voiceMemoAudioUrl(memo.id)} style={{ width: '100%' }} />
        </>
      )}

      {detail && memo.intent !== 'note' && memo.intent !== 'order' && memo.intent !== 'appointment' && (
        <>
          <div style={{ fontWeight: 600, color: '#111', marginBottom: 6 }}>Logged to</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            {logged ? (
              <span className="voicelog-candidate is-selected" style={{ cursor: 'default' }}>
                {logged.firstName} {logged.lastName}
                {logged.dob ? <span className="dob">DOB {formatDob(logged.dob)}</span> : null}
              </span>
            ) : (
              <span style={{ color: '#6b7280' }}>Patient details unavailable — open Time Logs to verify.</span>
            )}
            {memo.committedPatientId && onJumpToTimePatient && (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => onJumpToTimePatient(memo.committedPatientId)}
                title="Open this patient in Time Logs"
              >
                Time Logs ›
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 12, color: '#374151' }}>
            {parsed.minutes ? <span><strong>{parsed.minutes}</strong> min</span> : null}
            {parsed.activity_type ? <span>{parsed.activity_type}</span> : null}
            {parsed.program ? <span>{String(parsed.program).toUpperCase()}</span> : null}
          </div>

          <div style={{ color: '#374151', fontStyle: detail.transcript ? 'italic' : 'normal', marginBottom: 8 }}>
            {detail.transcript ? `“${detail.transcript}”` : 'No transcript available.'}
          </div>
          <audio
            controls
            preload="none"
            src={voiceMemoAudioUrl(memo.id)}
            style={{ width: '100%' }}
          />
        </>
      )}
    </div>
  );
}

function MemoRow({ memo, showUser, isAdmin, onCommitted, onDiscarded, onRerouted, onTransferred, onJumpToTimePatient }) {
  const [expanded, setExpanded] = useState(false);
  const reviewable = memo.status === 'needs_review' || memo.status === 'failed';
  // Committed rows drill into a read-only match-verification card.
  const expandable = reviewable || memo.status === 'committed';
  const summary = memo.status === 'committed' ? parsedSummary(memo) : '';
  const intentChip = memo.intent && memo.intent !== 'unclassified' ? (INTENT_CHIP[memo.intent] || memo.intent) : null;

  const head = (
    <>
      <StatusChip status={memo.status} />
      {intentChip && (
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
          color: '#4b5563', background: '#f3f4f6', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap',
        }}>
          {intentChip}
        </span>
      )}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' }}>
        {summary || memo.transcriptPreview || (memo.durationSec ? `${memo.durationSec}s memo` : 'Voice memo')}
      </span>
      {showUser && memo.userDisplayName && (
        <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>{memo.userDisplayName}</span>
      )}
      <span style={{ fontSize: 11, color: '#9ca3af', whiteSpace: 'nowrap' }}>{whenStr(memo.createdAt)}</span>
      {expandable && <span aria-hidden="true" style={{ color: '#9ca3af' }}>{expanded ? '▾' : '▸'}</span>}
    </>
  );

  return (
    <div className="voicelog-memo-row">
      {expandable ? (
        <button
          type="button"
          className="voicelog-memo-head"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {head}
        </button>
      ) : (
        <div className="voicelog-memo-head">
          {head}
        </div>
      )}
      {reviewable && expanded && (() => {
        const done = {
          onCommitted: (id, res) => { setExpanded(false); onCommitted(id, res); },
          onDiscarded: (id) => { setExpanded(false); onDiscarded(id); },
          onRerouted: (id, intent) => { setExpanded(false); onRerouted(id, intent); },
        };
        if (memo.intent === 'note') return <NoteReviewCard memo={memo} isAdmin={isAdmin} {...done} />;
        if (memo.intent === 'order') {
          return (
            <OrderReviewCard
              memo={memo}
              {...done}
              onTransferred={(id, res) => { setExpanded(false); onTransferred(id, res); }}
            />
          );
        }
        if (memo.intent === 'appointment') {
          return (
            <AppointmentReviewCard
              memo={memo}
              {...done}
              onTransferred={(id, res) => { setExpanded(false); onTransferred(id, res); }}
            />
          );
        }
        if (!memo.intent || memo.intent === 'timelog') return <ReviewCard memo={memo} {...done} />;
        return <UnsupportedCard memo={memo} onDiscarded={done.onDiscarded} onRerouted={done.onRerouted} />;
      })()}
      {memo.status === 'committed' && expanded && (
        <CommittedDetails memo={memo} onJumpToTimePatient={onJumpToTimePatient} />
      )}
    </div>
  );
}

export function VoiceLogPage({ currentUser, armRequest, onArmConsumed, onReviewCountChange, onJumpToTimePatient, onOpenOrderChat }) {
  const isAdmin = String(currentUser?.role || '').toLowerCase() === 'admin';

  // Recorder
  const [phase, setPhase] = useState('idle'); // idle | recording | uploading | saved
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [micError, setMicError] = useState('');
  const recorderRef = useRef(null);
  const finishingRef = useRef(false);
  const savedTimerRef = useRef(0);

  // Lists
  const [memos, setMemos] = useState([]);
  const [adminQueue, setAdminQueue] = useState(null); // null until first load (admin only)
  const [reviewCount, setReviewCount] = useState(0);
  const [notEnabled, setNotEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [failedUploads, setFailedUploads] = useState([]); // in-memory only (PHI)

  // ── Lists ──────────────────────────────────────────────────────────────
  const refreshLists = useCallback(async () => {
    try {
      const data = await listVoiceMemos({ status: 'all', scope: 'mine', limit: LIST_LIMIT });
      const server = data.memos || [];
      const serverIds = new Set(server.map((m) => m.id));
      // Keep optimistic rows the server hasn't shown us yet (upload just landed).
      setMemos((prev) => [...prev.filter((m) => m._optimistic && !serverIds.has(m.id)), ...server]);
      setReviewCount(data.reviewCount || 0);
      setNotEnabled(false);
      if (isAdmin) {
        const q = await listVoiceMemos({ status: 'needs_review', scope: 'practice', limit: 25 });
        setAdminQueue(q.memos || []);
      }
    } catch (e) {
      if (e && e.status === 404) setNotEnabled(true);
      // other errors: keep the current list; the next poll retries
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { refreshLists(); }, [refreshLists]);

  // Poll every 4s ONLY while a memo is still being processed AND the tab is
  // visible (the interval checks visibility so a hidden tab makes no requests;
  // visibilitychange refreshes immediately on return).
  const hasPending = memos.some((m) => NON_TERMINAL.has(m.status));
  useEffect(() => {
    if (notEnabled || !hasPending) return undefined;
    const tick = () => { if (document.visibilityState === 'visible') refreshLists(); };
    const timer = window.setInterval(tick, 4000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [hasPending, notEnabled, refreshLists]);

  // Report the review badge up to the shell.
  useEffect(() => { onReviewCountChange?.(reviewCount); }, [reviewCount, onReviewCountChange]);

  // ── Upload ─────────────────────────────────────────────────────────────
  async function postMemo(item) {
    const { base64 } = await blobToBase64(item.blob);
    return await createVoiceMemo({
      audioBase64: base64,
      mimeType: item.mimeType,
      durationSec: item.durationSec,
      idempotencyKey: item.idempotencyKey,
    });
  }

  function acceptSaved(res, item) {
    setMemos((prev) => [{
      id: res.id,
      status: res.status || 'uploaded',
      source: 'web',
      durationSec: item.durationSec,
      createdAt: new Date().toISOString(),
      parsed: null,
      reviewReasons: [],
      transcriptPreview: '',
      _optimistic: true,
    }, ...prev.filter((m) => m.id !== res.id)]);
    setPhase('saved');
    if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    savedTimerRef.current = window.setTimeout(() => {
      setPhase((p) => (p === 'saved' ? 'idle' : p));
    }, 1500);
  }

  async function uploadNew(item) {
    setPhase('uploading');
    try {
      const res = await postMemo(item);
      acceptSaved(res, item);
    } catch (e) {
      if (e && e.status === 404) {
        setNotEnabled(true);
        setPhase('idle');
        return;
      }
      // Keep the blob in memory as a retry chip — same idempotencyKey on retry.
      setFailedUploads((prev) => [...prev, { ...item, error: e.message || 'Upload failed', retrying: false }]);
      setPhase('idle');
    }
  }

  async function retryUpload(idempotencyKey) {
    const item = failedUploads.find((f) => f.idempotencyKey === idempotencyKey);
    if (!item || item.retrying) return;
    setFailedUploads((prev) => prev.map((f) => (f.idempotencyKey === idempotencyKey ? { ...f, retrying: true } : f)));
    try {
      const res = await postMemo(item); // SAME idempotencyKey — the server dedupes
      setFailedUploads((prev) => prev.filter((f) => f.idempotencyKey !== idempotencyKey));
      acceptSaved(res, item);
    } catch (e) {
      if (e && e.status === 404) { setNotEnabled(true); return; }
      setFailedUploads((prev) => prev.map((f) => (
        f.idempotencyKey === idempotencyKey ? { ...f, retrying: false, error: e.message || 'Upload failed' } : f
      )));
    }
  }

  // Warn before leaving while a recording/upload is pending or a failed upload
  // is still held in memory (closing the tab loses the audio).
  const pendingRef = useRef(false);
  pendingRef.current = phase === 'recording' || phase === 'uploading' || failedUploads.length > 0;
  useEffect(() => {
    const handler = (e) => {
      if (pendingRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  // ── Record / finish ────────────────────────────────────────────────────
  async function startRecording() {
    if (recorderRef.current || (phase !== 'idle' && phase !== 'saved')) return;
    setMicError('');
    setElapsed(0);
    setLevel(0);
    const rec = createVoiceRecorder({
      onLevel: setLevel,
      onElapsed: (sec) => {
        setElapsed(sec);
        // Hard cap: the recorder auto-finalizes internally; drive the UI too.
        if (sec >= MAX_SECONDS) finishRef.current();
      },
      maxSeconds: MAX_SECONDS,
    });
    recorderRef.current = rec;
    try {
      await rec.start();
      setPhase('recording');
    } catch (_) {
      recorderRef.current = null;
      setMicError('Microphone unavailable — check your browser permissions and try again.');
    }
  }

  async function finishRecording() {
    const rec = recorderRef.current;
    if (!rec || finishingRef.current) return;
    finishingRef.current = true;
    setPhase('uploading');
    let captured = null;
    try {
      captured = await rec.stop();
    } catch (e) {
      setPhase('idle');
      setMicError(e && e.message === 'empty recording'
        ? 'Nothing was recorded — try again.'
        : 'Recording failed — try again.');
    } finally {
      recorderRef.current = null;
      finishingRef.current = false;
      setLevel(0);
    }
    if (captured) {
      await uploadNew({
        blob: captured.blob,
        mimeType: captured.mimeType,
        durationSec: captured.durationSec,
        idempotencyKey: crypto.randomUUID(),
      });
    }
    setElapsed(0);
  }

  // Latest finish fn for the recorder's elapsed callback (avoids a stale closure).
  const finishRef = useRef(() => {});
  finishRef.current = finishRecording;

  // Cancel a live recording when the page unmounts (tab switch) so the mic
  // indicator clears — an unfinished memo is not worth keeping the mic open.
  useEffect(() => () => {
    if (recorderRef.current) { recorderRef.current.cancel(); recorderRef.current = null; }
    if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
  }, []);

  // One-shot arm signal (?rec=1 deep link / Alt+V): auto-start ONLY when the
  // mic permission is already granted — never trigger a permission prompt from
  // a synthetic signal; otherwise the page just renders ready-to-tap.
  const consumedArmRef = useRef(0);
  useEffect(() => {
    if (!armRequest || !armRequest.ts || armRequest.ts === consumedArmRef.current) return undefined;
    consumedArmRef.current = armRequest.ts;
    onArmConsumed?.();
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.permissions || !navigator.permissions.query) return;
        const st = await navigator.permissions.query({ name: 'microphone' });
        if (!cancelled && st.state === 'granted') startRecording();
      } catch (_) { /* permission API unsupported → ready-to-tap */ }
    })();
    return () => { cancelled = true; };
  }, [armRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── List mutations from review cards ───────────────────────────────────
  function markCommitted(id, res) {
    const flip = (m) => (m.id === id
      ? {
          ...m, status: 'committed',
          committedEntryId: res.entryId || null,
          committedNoteId: res.noteId || null,
          committedPatientId: res.patientId != null ? res.patientId : m.committedPatientId,
        }
      : m);
    setMemos((prev) => prev.map(flip));
    setAdminQueue((prev) => (prev ? prev.filter((m) => m.id !== id) : prev));
    setReviewCount((n) => Math.max(0, n - 1));
  }

  function markDiscarded(id) {
    setMemos((prev) => prev.filter((m) => m.id !== id));
    setAdminQueue((prev) => (prev ? prev.filter((m) => m.id !== id) : prev));
    setReviewCount((n) => Math.max(0, n - 1));
  }

  // Handed off to Order Chat: flip the row and jump the user into the chat
  // thread (the memo auto-commits when the chat completes).
  function markTransferred(id, res) {
    setMemos((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'transferred' } : m)));
    setAdminQueue((prev) => (prev ? prev.filter((m) => m.id !== id) : prev));
    setReviewCount((n) => Math.max(0, n - 1));
    if (onOpenOrderChat && res && res.sessionId) onOpenOrderChat(res.sessionId);
  }

  // Re-routed memo goes back through the worker ('transcribed' is a pending
  // state, so the existing poll picks up the fresh extraction automatically).
  function markRerouted(id, intent) {
    setMemos((prev) => prev.map((m) => (m.id === id
      ? { ...m, status: 'transcribed', intent, reviewReasons: [], parsed: null }
      : m)));
    setAdminQueue((prev) => (prev ? prev.filter((m) => m.id !== id) : prev));
    setReviewCount((n) => Math.max(0, n - 1));
  }

  // ── Render ─────────────────────────────────────────────────────────────
  if (notEnabled) {
    return (
      <section className="voicelog-page" role="region" aria-label="Voice Log">
        <div style={{ margin: '48px auto', maxWidth: 420, textAlign: 'center', color: '#4b5563' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🎙️</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 6 }}>
            Voice memos aren't enabled for this practice yet.
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            Ask your administrator to turn on voice time-logging.
          </div>
        </div>
      </section>
    );
  }

  const recording = phase === 'recording';
  const remaining = MAX_SECONDS - elapsed;
  const btnLabel = recording ? 'Tap to finish'
    : phase === 'uploading' ? 'Saving…'
    : phase === 'saved' ? 'Saved ✓'
    : 'Tap to record';

  return (
    <section className="voicelog-page" role="region" aria-label="Voice Log">
      <div style={{ padding: '14px 0 0' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#111' }}>Voice Log</div>
        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Say the program, minutes, activity, and patient — e.g. “CCM 20 minutes, caregiver discussion, for John Smith”
        </div>
      </div>

      <div className="voicelog-recorder">
        <button
          type="button"
          className={'voicelog-record-btn'
            + (recording ? ' is-recording' : '')
            + (phase === 'uploading' ? ' is-uploading' : '')
            + (phase === 'saved' ? ' is-saved' : '')}
          onClick={() => { if (recording) finishRecording(); else startRecording(); }}
          disabled={phase === 'uploading'}
          aria-label={btnLabel}
        >
          {recording && (
            <span
              className="voicelog-level-ring"
              aria-hidden="true"
              style={{ transform: `scale(${1 + Math.min(1, level) * 0.25})`, opacity: 0.35 + Math.min(1, level) * 0.65 }}
            />
          )}
          <span className="voicelog-record-glyph" aria-hidden="true">
            {phase === 'uploading' ? <span className="voicelog-spinner" style={{ width: 34, height: 34 }} />
              : phase === 'saved' ? '✓'
              : recording ? '⏹' : '🎙️'}
          </span>
          {recording && <span style={{ fontSize: 24, fontVariantNumeric: 'tabular-nums' }}>{mmss(elapsed)}</span>}
          <span>{btnLabel}</span>
        </button>
        {recording && elapsed > COUNTDOWN_AFTER && (
          <span className="voicelog-countdown">{Math.max(0, remaining)}s left</span>
        )}
        {phase === 'saved' && (
          <div className="voicelog-saved-flash" role="status">Saved ✓ — we'll log it for you</div>
        )}
        {micError && <div role="alert" style={{ color: '#b91c1c', fontSize: 13 }}>{micError}</div>}
      </div>

      {failedUploads.length > 0 && (
        <div>
          {failedUploads.map((f) => (
            <div key={f.idempotencyKey} className="voicelog-retry-row" role="alert">
              <span style={{ flex: 1 }}>
                Upload failed ({f.durationSec}s memo) — {f.error}
              </span>
              <button
                type="button"
                className="btn sm"
                disabled={f.retrying}
                onClick={() => retryUpload(f.idempotencyKey)}
              >
                {f.retrying ? 'Retrying…' : 'Retry'}
              </button>
              <button
                type="button"
                className="btn ghost sm"
                disabled={f.retrying}
                onClick={() => setFailedUploads((prev) => prev.filter((x) => x.idempotencyKey !== f.idempotencyKey))}
              >
                Discard
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111', margin: '10px 0 8px' }}>
          My recent memos
          {reviewCount > 0 && (
            <span className="voicelog-chip voicelog-chip--review" style={{ marginLeft: 8 }}>
              {reviewCount} to review
            </span>
          )}
        </div>
        {loading && (
          <div role="status" aria-live="polite" aria-label="Loading memos" className="skeleton">
            <div className="sk-line w70" />
            <div className="sk-line w40" />
            <div className="sk-line w70" />
          </div>
        )}
        {!loading && memos.length === 0 && (
          <div style={{ fontSize: 13, color: '#888', padding: '8px 0' }}>
            No memos yet — tap the button above and say your first one.
          </div>
        )}
        {memos.map((m) => (
          <MemoRow
            key={m.id}
            memo={m}
            isAdmin={isAdmin}
            onCommitted={markCommitted}
            onDiscarded={markDiscarded}
            onRerouted={markRerouted}
            onTransferred={markTransferred}
            onJumpToTimePatient={onJumpToTimePatient}
          />
        ))}
      </div>

      {isAdmin && (
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111', margin: '10px 0 8px' }}>
            Practice review queue
          </div>
          {adminQueue === null && (
            <div role="status" aria-live="polite" aria-label="Loading review queue" className="skeleton">
              <div className="sk-line w70" />
              <div className="sk-line w40" />
            </div>
          )}
          {adminQueue && adminQueue.length === 0 && (
            <div style={{ fontSize: 13, color: '#888', padding: '8px 0' }}>
              Nothing waiting for review.
            </div>
          )}
          {(adminQueue || []).map((m) => (
            <MemoRow
              key={m.id}
              memo={m}
              showUser
              isAdmin={isAdmin}
              onCommitted={markCommitted}
              onDiscarded={markDiscarded}
              onRerouted={markRerouted}
              onJumpToTimePatient={onJumpToTimePatient}
            />
          ))}
          <RoutingModeControl />
        </div>
      )}
    </section>
  );
}

// Admin-only practice setting: how memo intents are decided. Key must stay
// byte-identical with getVoiceMemoRoutingMode in server/services/voice.js.
const ROUTING_MODES = [
  ['hybrid', 'Hybrid', 'A spoken keyword ("note…", "order…") wins; everything else is auto-classified. Default.'],
  ['auto', 'Auto-classify', 'Every memo is classified by AI — no keywords needed.'],
  ['ritual', 'Keyword only', 'Only spoken keywords route; memos without one are time logs (pre-v2 behavior).'],
];

function RoutingModeControl() {
  const [mode, setMode] = useState(null); // null until loaded
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    ccmApi.getSettings()
      .then((s) => {
        if (cancelled) return;
        const v = s && s.voiceMemoRoutingMode;
        setMode(ROUTING_MODES.some(([k]) => k === v) ? v : 'hybrid');
      })
      .catch(() => { if (!cancelled) setMode('hybrid'); });
    return () => { cancelled = true; };
  }, []);

  // Saves are serialized (select disabled while one is in flight) so a slow
  // failure can never roll the UI back over a newer successful choice.
  async function save(next) {
    if (saving) return;
    const prev = mode;
    setMode(next);
    setSaving(true);
    setError('');
    try {
      await ccmApi.saveSettings({ voiceMemoRoutingMode: next });
    } catch (e) {
      setMode(prev);
      setError(e.message || 'Could not save the routing mode.');
    } finally {
      setSaving(false);
    }
  }

  const current = ROUTING_MODES.find(([k]) => k === mode);
  return (
    <div style={{ marginTop: 20, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 4 }}>Memo routing</div>
      <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        How memos are sorted into time logs, chart notes, and other requests.
      </div>
      <select
        value={mode || 'hybrid'}
        disabled={mode === null || saving}
        onChange={(e) => save(e.target.value)}
        aria-label="Voice memo routing mode"
        style={{
          padding: '8px 10px', borderRadius: 8, background: '#fff',
          border: '1px solid #d1d5db', fontSize: 13, fontFamily: 'inherit',
        }}
      >
        {ROUTING_MODES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </select>
      {current && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>{current[2]}</div>}
      {error && <div role="alert" style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>{error}</div>}
    </div>
  );
}
